"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Send } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType] = useState("Bathroom");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const validateZip = (value: string): boolean => {
    const isValid = /^\d{5}$/.test(value.trim());
    if (!isValid) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      return false;
    }
    setZipError("");
    return true;
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError("");
    const isZipValid = validateZip(zip);
    if (!consent) {
      setConsentError("Consent is required to submit your estimate request.");
    } else {
      setConsentError("");
    }
    if (!isZipValid || !consent) {
      return;
    }
    setIsSubmitting(true);
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const bodyParams = new URLSearchParams({
        name,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: pageUrl,
        form_location: "bathroom-remodeling-2-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: bodyParams
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setSubmitError("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setSubmitError("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
            Request an estimate
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Tell us about your bathroom project. We will review your goals and
            reach out to coordinate an initial consultation.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10">
          {submitSuccess ? <div className="text-center py-10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="font-serif text-2xl font-medium text-foreground">
                Thank you for your request
              </h3>
              <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                We have received your bathroom remodeling details. A specialist
                from Brookhaven Elevation Renovations will contact you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {submitError && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Submission issue</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="full-name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="full-name" name="name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone-number">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone-number" name="phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(470) 555-0149" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-address">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email-address" name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip-code">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip-code" name="zip" type="text" required maxLength={5} value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) validateZip(e.target.value);
              }} onBlur={e => validateZip(e.target.value)} placeholder="30319" aria-invalid={Boolean(zipError)} />
                  {zipError && <p className="text-xs text-destructive font-medium">
                      {zipError}
                    </p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project-type">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="project-type" name="project_type" value={projectType} disabled aria-readonly="true" className="flex h-9 w-full min-w-0 rounded-md border border-input bg-muted/50 px-3 py-1 text-sm shadow-xs text-foreground cursor-not-allowed opacity-80">
                    {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferred-timing">Preferred Timing</Label>
                  <select id="preferred-timing" name="timing" value={timing} onChange={e => setTiming(e.target.value)} className="flex h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none">
                    <option value="">Select a timeline</option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget-range">Budget Range</Label>
                <select id="budget-range" name="budget" value={budget} onChange={e => setBudget(e.target.value)} className="flex h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none">
                  <option value="">Select an estimated investment</option>
                  {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                      {opt}
                    </option>)}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-details">Project Details / Message</Label>
                <Textarea id="project-details" name="message" value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Share details regarding your existing bathroom, layout changes, finishes, or preferred fixtures..." />
              </div>

              <div className="space-y-1.5 pt-1">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent-checkbox" checked={consent} onCheckedChange={checked => {
                setConsent(checked === true);
                if (checked) setConsentError("");
              }} aria-invalid={Boolean(consentError)} />
                  <Label htmlFor="consent-checkbox" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive font-medium pl-7">
                    {consentError}
                  </p>}
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-7 py-3.5 rounded-lg shadow-sm hover:bg-primary/90 transition-all">
                  {isSubmitting ? "Sending request..." : <>
                      <span>Submit Estimate Request</span>
                      <Send className="size-4" />
                    </>}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}